import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { titleCase } from "title-case";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { GET_CARDS } from "graphql/card";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useAdminTools } from "hooks/useAdminTools";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { Inline } from "components/layout/reusableLayouts";
import CardBalanceBar, {
  CARD_BALANCE_BAR_WIDTH,
} from "components/common/cards/CardBalanceBar";
import CardRestrictionsCell from "components/common/cards/CardRestrictionsCell";
import CardStatusChip from "components/common/cards/CardStatusChip";
import { cardRequestTypeLabel } from "utils/cards/cardRequestTypeLabel";
import type { CardFilterCriteria } from "utils/cards/cardFilters";
import { cardRowData, EMPTY_CELL } from "utils/cards/cardRowData";
import type { CardRowData, CardRowNode } from "utils/cards/cardRowData";
import UserAvatar from "components/common/users/UserAvatar";

const extractCards = (data: {
  cards?: { edges?: { node: CardRowNode }[] };
}): CardRowData[] =>
  (data.cards?.edges ?? []).map(({ node }) => cardRowData(node));

interface CardsTableProps {
  filterData: CardFilterCriteria;
  /** Only the viewer's own cards, whatever their role. */
  mine?: boolean;
  handleOpenCardDetails: (cardId: string) => void;
  refreshCards?: number;
}

const CardsTable: React.FC<CardsTableProps> = ({
  filterData,
  mine = false,
  handleOpenCardDetails,
  refreshCards,
}) => {
  const { t } = useTranslation();
  const adminTools = useAdminTools();

  const variables = useMemo(
    () => ({
      first: DEFAULT_PAGINATION_SIZE,
      // The server resolves `mine` to the viewer, so /my never sends, or trusts, a client-side id.
      ...(mine ? { mine: true } : { userId: filterData.userId ?? undefined }),
      cardStatus: filterData.cardStatus.length
        ? filterData.cardStatus
        : undefined,
      cardType: filterData.cardType.length ? filterData.cardType : undefined,
      searchTerm: filterData.searchTerm,
    }),
    [filterData, mine],
  );

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_CARDS,
      initialVariables: variables,
      extractData: extractCards,
      refetchIndex: refreshCards,
    });

  const columns: DataColumn<CardRowData>[] = useMemo(
    () => [
      ...(adminTools
        ? [
            {
              id: "userName",
              header: t("Name"),
              width: "20%",
              cell: (card: CardRowData) => (
                <Inline gap="var(--space-3)" wrap={false}>
                  <UserAvatar size="sm" user={card.user} />
                  <Typography variant="body1">{card.userName}</Typography>
                </Inline>
              ),
            },
          ]
        : []),
      {
        id: "cardNumber",
        header: t("Card Number"),
        width: adminTools ? "18%" : "25%",
        maxWidth: 200,
        cell: (card) => ({
          content: card.cardNumber,
          caption: t(titleCase(card.type.toLowerCase())),
        }),
      },
      {
        id: "cardStatus",
        header: t("Card Status"),
        align: "center",
        width: adminTools ? "12%" : "14%",
        cell: (card) => {
          const requestLabel = card.isPendingRequest
            ? (cardRequestTypeLabel(card.pendingRequestType ?? undefined, t) ??
              t("Pending"))
            : null;
          return {
            content: (
              <CardStatusChip
                status={card.cardStatus}
                pendingTerms={card.pendingTerms}
              />
            ),
            caption: requestLabel
              ? t("PendingRequestCaption", { type: requestLabel })
              : undefined,
          };
        },
      },
      {
        id: "expiration",
        header: t("Expiration"),
        width: adminTools ? "12%" : "14%",
        cell: (card) => ({
          content: card.expiration ?? EMPTY_CELL,
          muted: !card.expiration,
          caption: card.cancelation
            ? t("CancelsOn", { date: card.cancelation })
            : undefined,
        }),
      },
      {
        id: "availableBalance",
        header: t("Available Balance"),
        width: CARD_BALANCE_BAR_WIDTH,
        cell: (card) => (
          <CardBalanceBar
            spent={card.spent}
            limit={card.limit}
            status={card.cardStatus}
            limitType={card.limitType}
          />
        ),
      },
      {
        id: "merchants",
        header: t("Merchants / Categories"),
        width: adminTools ? "18%" : "27%",
        cell: (card) => (
          <CardRestrictionsCell items={card.combinedCategoryAndMerchant} />
        ),
      },
    ],
    [t, adminTools],
  );

  return (
    <DataTable<CardRowData>
      columns={columns}
      rows={dataArray}
      getRowKey={(card) => card.id}
      loading={displayLoading}
      isFetchingMore={isInfiniteScrollFetching}
      onRowClick={(card) => handleOpenCardDetails(card.id)}
      onScroll={handleScroll}
      emptyState={t("No cards found")}
      loadingMoreText={t("Loading more cards")}
      stickyHeader
    />
  );
};

export default CardsTable;
